import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../lib/apiClient';
import { ConsoleLogger } from 'aws-amplify/utils';
import { API_ENDPOINTS, ATTR_TYPE_LABEL_MAP, CLASSIFICATION_MARKET_TARGET_ATTR_NAME, FIXED_ATTR_NAME_PROJECT, FIXED_ATTR_NAME_POWERTRAIN, FIXED_ATTR_NAME_SHIMUKE, FIXED_ATTR_NAME_LOT } from '../constants';
import { getCognitoUserEmail } from '../context/UserContext';
import { shareInFlightRequest } from '../lib/sharedApiRequest';

// 共通項目用属性ID
export const ATTRIBUTE_ID_REPORT = 1;
export const ATTRIBUTE_ID_PROJECT = 2;
export const ATTRIBUTE_ID_POWERTRAIN = 3;
export const ATTRIBUTE_ID_GRADE = 4;
export const ATTRIBUTE_ID_SHIMUKE = 5;
export const ATTRIBUTE_ID_LOT = 6;

const FIXED_ATTR_ID_SET = new Set<number>([
    ATTRIBUTE_ID_REPORT,
    ATTRIBUTE_ID_PROJECT,
    ATTRIBUTE_ID_POWERTRAIN,
    ATTRIBUTE_ID_GRADE,
    ATTRIBUTE_ID_SHIMUKE,
    ATTRIBUTE_ID_LOT,
]);

const FIXED_ATTR_FALLBACK_NAME_BY_ID: Record<number, string> = {
    [ATTRIBUTE_ID_REPORT]: CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
    [ATTRIBUTE_ID_PROJECT]: FIXED_ATTR_NAME_PROJECT,
    [ATTRIBUTE_ID_POWERTRAIN]: FIXED_ATTR_NAME_POWERTRAIN,
    [ATTRIBUTE_ID_GRADE]: '自由入力欄',
    [ATTRIBUTE_ID_SHIMUKE]: FIXED_ATTR_NAME_SHIMUKE,
    [ATTRIBUTE_ID_LOT]: FIXED_ATTR_NAME_LOT,
};

// 本文最大文字数（デフォルト）
export const MAX_BODY_LENGTH = 260;

// マスタリフレッシュ用atom
export const refreshMasterOptionsState = atom(0);
 
// リフレッシュサイクル識別子（同一サイクル中のforce取得を集約）
let masterRefreshToken = 0;
export const bumpMasterRefreshToken = () => { masterRefreshToken++; };
 
// 診断用: 実ネットワークリクエストの通番
let masterDebugReqSeq = 0;

// マスタ値の選択肢定義
export interface OptionsSelectList {
    label: string;
    value: string;
    department?: string;  // 部門名（文字列）
    team?: string;        // チーム名（文字列）
    color?: string;
    // チームモード用: 各オプションごとの本文最大文字数（未設定時は undefined）
    body_max_length?: number;
    enlight_sync_default?: boolean | number | string | null;
    status?: string; // APIから返る 'Y' | 'N' などを想定（未設定は表示対象）
    // 互換のために残す（今後は使用しない想定）
    department_id?: number;
    team_id?: number;
};

// マスタ値の型定義
export interface OptionsBase {
    attr_id: number;
    // 課/チーム固有の枝番 attr_id が、どの共通属性に属するかを示すAPI項目
    logical_attr_id?: number;
    attr_name?: string;
    attr_type?: number;
    set_color?: number;
    label: string;
    department_id: number;
    team_id?: number;
    type?: string;
    required: boolean;
    status: boolean;
    // Text input constraints
    min_length?: number;
    max_length?: number;
    // チームモード用: 本文最大文字数（未設定時は undefined）
    body_max_length?: number;
    options?: OptionsSelectList[];
    multiple_select?: boolean;
    use_for_sort?: boolean | number;
    visible?:boolean;
};

export const attrTypeLabelMap: { [key: number]: string } = ATTR_TYPE_LABEL_MAP;
// --- 固定項目 ---

// 汎用API取得関数
const fetchOptionsFromAPI = async (
    endpoint: string,
    fallbackData: any,
    requireArrayResponse = false,
): Promise<any> => {
    try {
        const response = await apiClient.get(endpoint);
        if (requireArrayResponse && !Array.isArray(response.data)) {
            throw new Error('Invalid organization master response');
        }
        return response.data;
    } catch (error) {
        console.error(`Failed to fetch data from ${endpoint}:`, error);
        // Organization read failures are not a successful empty master.
        // Let the hook retain its last successful data and show an error.
        if (requireArrayResponse) throw error;
        return fallbackData;
    }
};

/* =========================
   課（Division）: キャッシュ対応
 * ========================= */
let divisionCache: OptionsSelectList[] | null = null;
let divisionCachePromise: Promise<OptionsSelectList[]> | null = null;
let divisionCacheToken: number | null = null;
let divisionReqSeq = 0;
let divisionLatestReq = 0;
export const invalidateDivisionOptionsCache = () => {
    // 進行中の取得(divisionCachePromise)はここでは破棄しない。
    // 破棄すると呼び出し元から見えなくなり、同じAPIへの重複リクエストが発生するため。
    divisionCache = null;
};

// 課の選択肢を取得する関数（キャッシュ対応）
const fetchDivisionOptions = async (force = false): Promise<OptionsSelectList[]> => {
    const fallbackData: OptionsSelectList[] = [];

    if (!force && divisionCache) return divisionCache;
    // 同一リフレッシュサイクル内で既に取得済みなら再取得しない
    if (force && divisionCache && divisionCacheToken === masterRefreshToken) return divisionCache;
    if (divisionCachePromise) return divisionCachePromise;

    const reqId = ++divisionReqSeq;
    divisionLatestReq = reqId;
    divisionCachePromise = fetchOptionsFromAPI(API_ENDPOINTS.GET_DEPARTMENTS, fallbackData, true)
        .then((data: OptionsSelectList[]) => {
            if (reqId === divisionLatestReq) {
                divisionCache = data;
                divisionCacheToken = masterRefreshToken;
            }
            divisionCachePromise = null;
            return data;
        })
        .catch((err) => {
            divisionCachePromise = null;
            throw err;
        });

    return divisionCachePromise;
};

const divisionOptionsState = atom<OptionsSelectList[]>([]);
export const useDivisionOptions = () => {
    const [divisionOptionsRaw, setDivisionOptionsRaw] = useAtom(divisionOptionsState);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue, setRefreshValue] = useAtom(refreshMasterOptionsState);

    // 外部からリフレッシュをトリガーする関数
    // - キャッシュ無効化
    // - masterRefreshToken を進めて「新しいリフレッシュサイクル」にする
    // - refreshMasterOptionsState を更新して購読側を再読み込みさせる
    const refreshDivisionOptions = () => {
        invalidateDivisionOptionsCache();
        bumpMasterRefreshToken();
        setRefreshValue(Date.now());
    };


    // filtered: status が "N" のものは除外（未設定は表示）
    // 並び順: ラベルのアルファベット順（ABC順。数値は自然順に考慮, 例: XQ2 < XQ10）
    const divisionOptions = useMemo(() => {
        const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
        return [...(divisionOptionsRaw ?? [])]
            .filter((item: any) => String(item?.status ?? '').toUpperCase() !== 'N')
            .sort((a: any, b: any) =>
                collator.compare(
                    String(a?.label ?? a?.value ?? ''),
                    String(b?.label ?? b?.value ?? '')
                )
            );
    }, [divisionOptionsRaw]);

    useEffect(() => {
        const loadDivisionOptions = async () => {
            // 既に保持済みかつ明示リフレッシュでない場合はスキップ
            if ((divisionOptionsRaw?.length ?? 0) > 0 && !refreshValue) return;

            setIsLoading(true);
            setError(null);
            try {
                const options = await fetchDivisionOptions(Boolean(refreshValue));
                // 取得結果（raw）を保持
                setDivisionOptionsRaw(options);
            } catch (err) {
                setError('課の選択肢の取得に失敗しました');
                console.error('Failed to load division options:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadDivisionOptions();
    }, [refreshValue, setDivisionOptionsRaw]);

    return { divisionOptions, divisionOptionsRaw, setDivisionOptions: setDivisionOptionsRaw, isLoading, error, refreshDivisionOptions };
};

/* =========================
   チーム（Team）: キャッシュ対応
 * ========================= */
let teamCache: OptionsSelectList[] | null = null;
let teamCachePromise: Promise<OptionsSelectList[]> | null = null;
let teamCacheToken: number | null = null;
let teamReqSeq = 0;
let teamLatestReq = 0;
export const invalidateTeamOptionsCache = () => {
    // 進行中の取得はここでは破棄しない（重複リクエスト防止）
    teamCache = null;
};

const fetchTeamOptions = async (force = false): Promise<OptionsSelectList[]> => {
    const fallbackData: OptionsSelectList[] = [];

    if (!force && teamCache) return teamCache;
    if (force && teamCache && teamCacheToken === masterRefreshToken) return teamCache;
    if (teamCachePromise) return teamCachePromise;

    const reqId = ++teamReqSeq;
    teamLatestReq = reqId;
    teamCachePromise = fetchOptionsFromAPI(API_ENDPOINTS.GET_ALL_TEAMS, fallbackData, true)
        .then((data: OptionsSelectList[]) => {
            if (reqId === teamLatestReq) {
                teamCache = data;
                teamCacheToken = masterRefreshToken;
            }
            teamCachePromise = null;
            return data;
        })
        .catch((err) => {
            teamCachePromise = null;
            throw err;
        });

    return teamCachePromise;
};

const teamOptionsState = atom<OptionsSelectList[]>([]);
export const useTeamOptions = () => {
    const [teamOptionsRaw, setTeamOptionsRaw] = useAtom(teamOptionsState);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue, setRefreshValue] = useAtom(refreshMasterOptionsState);

    const refreshTeamOptions = () => setRefreshValue(Date.now());

    // filtered: status が "N" のものは除外（未設定は表示）
    const teamOptions = useMemo(
        () => (teamOptionsRaw ?? []).filter(
            (item: any) => String(item?.status ?? '').toUpperCase() !== 'N'
        ),
        [teamOptionsRaw]
    );

    useEffect(() => {
        const loadTeamOptions = async () => {
            // 既に保持済みかつ明示リフレッシュでない場合はスキップ
            if ((teamOptionsRaw?.length ?? 0) > 0 && !refreshValue) return;

            setIsLoading(true);
            setError(null);
            try {
                const options = await fetchTeamOptions(Boolean(refreshValue));
                setTeamOptionsRaw(options);
            } catch (err) {
                setError('チームの選択肢の取得に失敗しました');
                console.error('Failed to load team options:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadTeamOptions();
    }, [refreshValue, setTeamOptionsRaw]);

    return { teamOptions, teamOptionsRaw, setTeamOptions: setTeamOptionsRaw, isLoading, error, refreshTeamOptions };
};

/* =========================
   ユーザー（User）: 常に最新を取得（キャッシュ不使用）
 * ========================= */

// 互換API（常に最新を取得）
const normalizeNullableText = (value: any): string => {
    const text = String(value ?? '').normalize('NFKC').trim();
    const upperText = text.toUpperCase();
    if (
        !text ||
        upperText === 'NULL' ||
        upperText === '[NULL]' ||
        upperText === 'NONE' ||
        upperText === '[NONE]' ||
        upperText === 'UNDEFINED' ||
        upperText === '[UNDEFINED]'
    ) {
        return '';
    }
    return text;
};

const firstNullableText = (...values: any[]): string => {
    for (const value of values) {
        const text = normalizeNullableText(value);
        if (text) return text;
    }
    return '';
};

const fetchUserOptions = async (_force = false): Promise<OptionsSelectList[]> => {
    const fallbackData: OptionsSelectList[] = [];
    let data: any = fallbackData;
    try {
        const mailAddress = await getCognitoUserEmail();
        const normalizedMail = String(mailAddress ?? '').trim().toLowerCase();
        const response = await shareInFlightRequest(
            `get-all-user-info:preferred-false:${normalizedMail}`,
            () => apiClient.get(API_ENDPOINTS.GET_ALL_USER_INFO as string, {
                params: mailAddress
                    ? { mail_address: mailAddress, apply_preferred_team: false }
                    : { apply_preferred_team: false },
            }),
        );
        data = response.data;
    } catch (error) {
        console.error(`Failed to fetch data from ${API_ENDPOINTS.GET_ALL_USER_INFO}:`, error);
        data = fallbackData;
    }

    // APIレスポンスを配列に正規化（users / Users / list / data / 直配列 に対応）
    const list: any[] = Array.isArray(data)
        ? data
        : (Array.isArray((data as any)?.users) ? (data as any).users
        : Array.isArray((data as any)?.Users) ? (data as any).Users
        : Array.isArray((data as any)?.list)  ? (data as any).list
        : Array.isArray((data as any)?.data)  ? (data as any).data
        : []);

    // { label: "姓 名", value: "mail_address" } のイメージで正規化
    // value は「論理ユーザーID」としてメールアドレスを優先的に利用する
    const seen = new Set<string>();
    const opts: OptionsSelectList[] = list
        .map((u: any) => {
            const last  = String(u?.last_name  ?? u?.lastName  ?? '').trim();
            const first = String(u?.first_name ?? u?.firstName ?? '').trim();
            const name  = [last, first].filter(Boolean).join(' ') || String(u?.name ?? '').trim();

            const email = String(u?.mail_address ?? u?.mailAddress ?? u?.email ?? '').trim();

            // value は「ユーザー識別子」としてメールを使う
            const value = email || name || '';

            const department = firstNullableText(u?.department, u?.department_name);
            const team       = firstNullableText(
                // preferred_team_* を通常所属チームより優先する
                u?.preferred_team_label,
                u?.preferredTeamLabel,
                u?.preferred_team?.team_label,
                u?.preferredTeam?.teamLabel,
                u?.preferred_team_id,
                u?.preferredTeamId,
                u?.preferred_team?.team_id,
                u?.preferredTeam?.teamId,

                // preferred_team_* が無い場合だけ通常所属チームを使う
                u?.team,
                u?.team_name,
                u?.teamName,
                u?.team_label,
                u?.teamLabel
            );
            const status     = typeof u?.status === 'string' ? u.status : undefined;

            // label は画面表示用：氏名があれば氏名、無ければメール
            const labelBase = name || email || '';

            const out: OptionsSelectList = {
                label: labelBase,
                value,
                department: department || undefined,
                team: team || undefined,
                status,
            };
            return out;
        })
        .filter(o => !!o.label && !!o.value)
        .filter(o => String(o.status ?? '').toUpperCase() !== 'N')
        .filter(o => {
            // 重複判定は value（=メールアドレス）をベースに行う
            const key = o.value;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

    return opts;
};

// グローバル状態（キャッシュではなく直近取得結果の保持のみ）
const userOptionsState = atom<OptionsSelectList[]>([]);

// 互換のための no-op（インターフェース維持）
export const invalidateUserOptionsCache = () => { /* no-op: 常に最新取得のためキャッシュ無効 */ };

export const useUserOptions = () => {
    const [userOptionsRaw, setUserOptionsRaw] = useAtom(userOptionsState);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue, setRefreshValue] = useAtom(refreshMasterOptionsState);

    // 外部からリフレッシュをトリガーする関数（最新取得を再実行）
    const refreshUserOptions = () => setRefreshValue(Date.now());

    // filtered: status が "N" のものは除外（未設定は表示）
    const userOptions = (userOptionsRaw ?? []).filter(
        (item: any) => String(item?.status ?? '').toUpperCase() !== 'N'
    );

    useEffect(() => {
        let cancelled = false;
        const loadUserOptions = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // 常に最新を取得（キャッシュ不使用）
                const options = await fetchUserOptions(false);
                if (cancelled) return;
                setUserOptionsRaw(options);
            } catch (err) {
                setError('ユーザー一覧の取得に失敗しました');
                console.error('Failed to load user options:', err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadUserOptions();
        return () => { cancelled = true; };
    }, [refreshValue, setUserOptionsRaw]);

    return { userOptions, userOptionsRaw, setUserOptions: setUserOptionsRaw, isLoading, error, refreshUserOptions };
};

/* =========================
   属性（Attributes）: 既存キャッシュ
 * ========================= */
// 属性オプションAPIキャッシュ
let attributesCache: OptionsBase[] | null = null;
let attributesCachePromise: Promise<OptionsBase[]> | null = null;
let attributesCacheToken: number | null = null;
let attributesReqSeq = 0;
let attributesLatestReq = 0;
export const invalidateAttributeOptionsCache = () => {
    // 進行中の取得はここでは破棄しない（重複リクエスト防止）
    attributesCache = null;
    fetchedAttributeOrgKeys.clear();
};

/**
 * 属性の選択肢を取得する関数（キャッシュ対応）
 * 仕様:
 *  - GET_ALL_ATTRIBUTES を1回だけ呼び、全課・全チームの属性を一括取得する。
 *  - 課ごとの GET_ATTRIBUTES の並列呼び出しは、DB接続数を急増させるため行わない。
 *  - 同一リフレッシュサイクル内の呼び出しは Promise / キャッシュを共有する。
 */
export const fetchAttributeOptions = async (force = false): Promise<OptionsBase[]> => {
    // force 指定がなく、キャッシュ済みなら即返す
    if (!force && attributesCache) return attributesCache;
    // 同一リフレッシュサイクル中に既に取得済みなら再取得しない
    if (force && attributesCache && attributesCacheToken === masterRefreshToken) return attributesCache;

    // 進行中の取得があればそれを待つ（force でも共有）
    if (attributesCachePromise) return attributesCachePromise;

    // 取得開始（まず Promise を設定）
    const reqId = ++attributesReqSeq;
    attributesLatestReq = reqId;
    attributesCachePromise = (async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.GET_ALL_ATTRIBUTES);
            const data = response?.data;
            const attributes = Array.isArray(data)
                ? data as OptionsBase[]
                : Array.isArray((data as any)?.data)
                    ? (data as any).data as OptionsBase[]
                    : Array.isArray((data as any)?.list)
                        ? (data as any).list as OptionsBase[]
                        : null;

            if (!attributes) {
                throw new Error(`Unexpected response from ${API_ENDPOINTS.GET_ALL_ATTRIBUTES}`);
            }

            if (reqId === attributesLatestReq) {
                attributesCache = attributes;
                attributesCacheToken = masterRefreshToken;
            }
            return attributes;
        } catch (err) {
            console.error(`Failed to fetch data from ${API_ENDPOINTS.GET_ALL_ATTRIBUTES}:`, err);
            // 取得失敗を空配列へ変換すると、既存の表示まで消えてしまう。
            // 呼び出し元へ失敗を伝え、保持中の atom は更新しない。
            throw err;
        } finally {
            attributesCachePromise = null;
        }
    })().catch((err) => {
        attributesCachePromise = null;
        throw err;
    });

    return attributesCachePromise;
};

/* =========================
   属性（Attributes）: 課・チーム単位のスコープ付き取得
 * ========================= */
// 既にスコープ取得済みの (department_id:team_id) の組み合わせ
const fetchedAttributeOrgKeys = new Set<string>();
// 組織単位の取得中Promise（同一組織への同時要求を集約する。invalidate時にも破棄しない＝重複リクエスト防止）
const attributeOrgFetchPromises = new Map<string, Promise<void>>();

export const hasFetchedAttributesForOrg = (departmentId: number, teamId: number): boolean =>
    fetchedAttributeOrgKeys.has(`${departmentId}:${teamId}`);

const attributeMergeKey = (a: any): string =>
    `${a?.attr_id}__${a?.department_id ?? 0}__${a?.team_id ?? 0}`;

// 新規取得分を既存キャッシュへ蓄積マージ（同一キーは新しい方で上書き）
const mergeAttributesIntoCache = (incoming: OptionsBase[]) => {
    const map = new Map<string, OptionsBase>();
    (attributesCache ?? []).forEach((a) => map.set(attributeMergeKey(a), a));
    (incoming ?? []).forEach((a) => map.set(attributeMergeKey(a), a));
    attributesCache = Array.from(map.values());
};

/**
 * 指定した課・チームの属性のみを取得する関数（GET_ATTRIBUTES）。
 * 仕様:
 *  - 取得結果は attributesCache（fetchAttributeOptions と共有）へ蓄積マージする。
 *    これにより、固定項目の枝番解決など既存の名前ベース正規化ロジックをそのまま利用できる。
 *  - 同一 (department_id, team_id) を既に取得済みなら再取得しない（force 指定時を除く）。
 *  - 同一組織への同時要求は Promise を共有する。
 */
export const fetchAttributeOptionsForOrg = async (
    departmentId: number,
    teamId: number,
    force = false,
): Promise<OptionsBase[]> => {
    const key = `${departmentId}:${teamId}`;

    if (!force && fetchedAttributeOrgKeys.has(key)) return attributesCache ?? [];
    if (force && fetchedAttributeOrgKeys.has(key) && attributesCacheToken === masterRefreshToken) return attributesCache ?? [];

    const inFlight = attributeOrgFetchPromises.get(key);
    if (inFlight) {
        await inFlight;
        return attributesCache ?? [];
    }

    const promise = (async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.GET_ATTRIBUTES, {
                params: { department_id: departmentId, team_id: teamId },
            });
            const data = response?.data;
            const attributes = Array.isArray(data)
                ? data as OptionsBase[]
                : Array.isArray((data as any)?.data)
                    ? (data as any).data as OptionsBase[]
                    : Array.isArray((data as any)?.list)
                        ? (data as any).list as OptionsBase[]
                        : [];
            mergeAttributesIntoCache(attributes);
            attributesCacheToken = masterRefreshToken;
            fetchedAttributeOrgKeys.add(key);
        } catch (err) {
            console.error(`Failed to fetch data from ${API_ENDPOINTS.GET_ATTRIBUTES} (department_id=${departmentId}, team_id=${teamId}):`, err);
            throw err;
        } finally {
            attributeOrgFetchPromises.delete(key);
        }
    })();
    attributeOrgFetchPromises.set(key, promise);

    await promise;
    return attributesCache ?? [];
};

 /* =========================
    属性共通オプション: キャッシュ対応
 * ========================= */
let commonOptionsCache: OptionsBase[] | null = null;
let commonOptionsCachePromise: Promise<OptionsBase[]> | null = null;
let commonOptionsCacheToken: number | null = null;
let commonOptionsReqSeq = 0;
let commonOptionsLatestReq = 0;
export const invalidateCommonOptionsCache = () => {
    // 進行中の取得はここでは破棄しない（重複リクエスト防止）
    commonOptionsCache = null;
};

 // /get_attribute_options_common から取得（fetchOptionsFromAPI + fetchDivisionOptions 構成に準拠）
export const fetchCommonOptios = async (force = false): Promise<OptionsBase[]> => {
    const fallbackData: OptionsBase[] = [];

    if (!force && commonOptionsCache) return commonOptionsCache;
    if (force && commonOptionsCache && commonOptionsCacheToken === masterRefreshToken) return commonOptionsCache;
    if (commonOptionsCachePromise) return commonOptionsCachePromise;

    const endpoint = '/get_attribute_options_common';
    const reqId = ++commonOptionsReqSeq;
    commonOptionsLatestReq = reqId;
    commonOptionsCachePromise = fetchOptionsFromAPI(endpoint, fallbackData)
        .then((data: OptionsBase[]) => {
            if (reqId === commonOptionsLatestReq) {
                commonOptionsCache = data;
                commonOptionsCacheToken = masterRefreshToken;
            }
            commonOptionsCachePromise = null;
            return data;
        })
        .catch((err) => {
            commonOptionsCachePromise = null;
            throw err;
        });

    return commonOptionsCachePromise;
};

 // 共通: ステータス有効判定
const isItemEnabled = (v: any) => {
    if (typeof v === 'string') return String(v).toUpperCase() !== 'N';
    return v !== false;
};

// 共通: 子オプションのフィルタ（status='N' を除外）
const sanitizeItem = (item: any) => ({
    ...item,
    options: (item?.options ?? []).filter((opt: any) => String(opt?.status ?? '').toUpperCase() !== 'N')
});

type AttributeNormalizeOptions = {
    includeAttrTypeInKey?: boolean;
    keepFallbackCandidate?: boolean;
};

const toOrgId = (v: any): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const getAttrNameKey = (attr: any): string => {
    return String((attr as any)?.attr_name ?? (attr as any)?.label ?? '').trim();
};

const getAttrGroupKey = (attr: any, includeAttrTypeInKey = true): string => {
    const name = getAttrNameKey(attr);
    if (!includeAttrTypeInKey) return name;
    const t = Number((attr as any)?.attr_type);
    return `${name}__${Number.isFinite(t) ? t : ''}`;
};

const dedupeOptionsByValueOrLabel = (options: any[]): any[] => {
    const seen = new Set<string>();
    const out: any[] = [];
    for (const opt of options ?? []) {
        const value = String((opt as any)?.value ?? '').trim();
        const label = String((opt as any)?.label ?? '').trim();
        const key = value !== '' ? `v:${value}` : `l:${label}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(opt);
    }
    return out;
};

const pickOptionsByTeam = (options: any[], selectedTeamId: number): any[] => {
    const teamId = toOrgId(selectedTeamId);
    const all = Array.isArray(options) ? options : [];

    const teamOptions = all.filter((opt: any) => toOrgId((opt as any)?.team_id) === teamId);
    if (teamOptions.length > 0) return dedupeOptionsByValueOrLabel(teamOptions);

    const commonOptions = all.filter((opt: any) => toOrgId((opt as any)?.team_id) === 0);
    return dedupeOptionsByValueOrLabel(commonOptions);
};

const pickPreferredAttribute = (candidates: any[], selectedDepartmentId: number, selectedTeamId: number): any | null => {
    const depId = toOrgId(selectedDepartmentId);
    const teamId = toOrgId(selectedTeamId);
    const list = Array.isArray(candidates) ? candidates : [];

    const exact = list.filter((attr: any) =>
        toOrgId((attr as any)?.department_id) === depId &&
        toOrgId((attr as any)?.team_id) === teamId
    );
    if (exact.length > 0) {
        return [...exact].sort((a: any, b: any) => (Number((b as any)?.options?.length ?? 0) - Number((a as any)?.options?.length ?? 0)))[0];
    }

    const common = list.filter((attr: any) =>
        toOrgId((attr as any)?.department_id) === 0 &&
        toOrgId((attr as any)?.team_id) === 0
    );
    if (common.length > 0) {
        return [...common].sort((a: any, b: any) => (Number((b as any)?.options?.length ?? 0) - Number((a as any)?.options?.length ?? 0)))[0];
    }

    return null;
};

/**
 * GET /get_attributes?department_id={id} で混在返却される属性を、
 * 画面選択中の department/team 向けに 1属性=1行へ正規化する。
 *
 * 優先順位:
 * 1) department_id=selectedDepartmentId && team_id=selectedTeamId
 * 2) department_id=0 && team_id=0
 * 3) それ以外は除外（keepFallbackCandidate=true のときのみ先頭候補を採用）
 *
 * options:
 * - option.team_id=selectedTeamId を優先
 * - なければ option.team_id=0 を採用
 * - value/label ベースで重複排除
 */
export const normalizeAttributesForSelection = (
    attributes: OptionsBase[] | null | undefined,
    selectedDepartmentId: number,
    selectedTeamId: number,
    opts?: AttributeNormalizeOptions
): OptionsBase[] => {
    const includeAttrTypeInKey = opts?.includeAttrTypeInKey ?? true;
    const keepFallbackCandidate = opts?.keepFallbackCandidate ?? false;
    const list = Array.isArray(attributes) ? attributes : [];

    const groups = new Map<string, any[]>();
    list.forEach((attr: any) => {
        const key = getAttrGroupKey(attr, includeAttrTypeInKey);
        if (!key) return;
        const arr = groups.get(key) ?? [];
        arr.push(attr);
        groups.set(key, arr);
    });

    const out: OptionsBase[] = [];
    groups.forEach((candidates) => {
        const picked = pickPreferredAttribute(candidates, selectedDepartmentId, selectedTeamId)
            ?? (keepFallbackCandidate ? candidates[0] : null);
        if (!picked) return;

        const groupedOptions = candidates.flatMap((it: any) => (Array.isArray((it as any)?.options) ? (it as any).options : []));
        const normalizedOptions = pickOptionsByTeam(groupedOptions, selectedTeamId);
        out.push({
            ...(picked as any),
            options: normalizedOptions,
        } as OptionsBase);
    });

    return out;
};

// 共通: 属性ごとの取得・フィルタを行う汎用フック
const useAttrOptions = (
    attrId: number,
    stateAtom: any,
    errorMessage: string,
    org?: { department_id?: number; team_id?: number },
) => {
    const [raw, setRaw] = useAtom(stateAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue, setRefreshValue] = useAtom(refreshMasterOptionsState);

    const refresh = () => setRefreshValue(Date.now());

    const depId = toOrgId(org?.department_id ?? 0);
    const teamId = toOrgId(org?.team_id ?? 0);
    const hasOrg = depId > 0;

    // TS 対策: raw が配列でないケースに備えてガード
    const list: any[] = Array.isArray(raw) ? (raw as any[]) : [];
    const options = list
        .filter((item: any) => Number(item?.attr_id) === attrId)
        .filter((item: any) => isItemEnabled((item as any)?.status))
        .map(sanitizeItem);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            // 組織指定がある場合は組織単位の取得済み判定、無い場合は従来通り保持データで判定
            if (hasOrg) {
                if (hasFetchedAttributesForOrg(depId, teamId) && !refreshValue) return;
            } else if ((list?.length ?? 0) > 0 && !refreshValue) {
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const attrs = hasOrg
                    ? await fetchAttributeOptionsForOrg(depId, teamId, Boolean(refreshValue))
                    : await fetchAttributeOptions(Boolean(refreshValue));
                if (cancelled) return;
                const allAttrs = Array.isArray(attrs) ? attrs : [];
                const byAttrId = allAttrs.filter((a: any) => Number(a?.attr_id) === attrId);

                let target = byAttrId;
                if (FIXED_ATTR_ID_SET.has(Number(attrId))) {
                    // 固定項目は attr_id が枝番化されるため、attr_name（必要時はfallback名）で拾い直す
                    const canonicalNames = new Set<string>(
                        byAttrId
                            .map((a: any) => getAttrNameKey(a))
                            .filter(Boolean)
                    );
                    const fallbackName = FIXED_ATTR_FALLBACK_NAME_BY_ID[Number(attrId)];
                    if (fallbackName) canonicalNames.add(fallbackName);

                    if (canonicalNames.size > 0) {
                        target = allAttrs.filter((a: any) => canonicalNames.has(getAttrNameKey(a)));
                    }
                }
                setRaw(target);
            } catch (err) {
                setError(errorMessage);
                console.error(errorMessage, err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [attrId, refreshValue, setRaw, hasOrg, depId, teamId]);

    return { options, raw, setRaw, isLoading, error, refresh };
};

// 汎用: 属性オプション用のラッパーフックを生成（特定名で返却）
type NamedHookConfig = {
    optionsKey: string;
    rawKey: string;
    setKey: string;
    refreshKey: string;
};

const createNamedAttrHook = (attrId: number, stateAtom: any, errorMessage: string, config: NamedHookConfig) => {
    // フック利用側が任意で { department_id, team_id } を渡せるようにする（後方互換: 引数省略可）
    return (org?: { department_id?: number; team_id?: number }) => {
        const base = useAttrOptions(attrId, stateAtom, errorMessage, org);
        const depId = toOrgId(org?.department_id ?? 0);
        const teamId = toOrgId(org?.team_id ?? 0);
        const hasOrg = depId > 0;
        const rawList = Array.isArray(base.raw) ? (base.raw as any[]) : [];

        const patchedRaw = hasOrg
            // 固定項目は同名で attr_type が混在する返却があり得るため、name単位で1行に正規化する
            ? normalizeAttributesForSelection(rawList, depId, teamId, { includeAttrTypeInKey: false })
            : rawList;
        const patchedOptions = hasOrg
            ? (patchedRaw as any[]).filter((item: any) => isItemEnabled((item as any)?.status)).map(sanitizeItem)
            : base.options;

        const result: any = {
            isLoading: base.isLoading,
            error: base.error,
        };
        (result as any)[config.optionsKey] = patchedOptions;
        (result as any)[config.rawKey] = patchedRaw;
        (result as any)[config.setKey] = base.setRaw;
        (result as any)[config.refreshKey] = base.refresh;
        return result;
    };
};

// 週報分類（attr_id=1）: API（/get_all_attributes）から取得
export const reportTypeOptionsState = atom<OptionsBase[]>([]);
export const useReportTypeOptions = createNamedAttrHook(
    ATTRIBUTE_ID_REPORT,
    reportTypeOptionsState,
    '週報分類の取得に失敗しました',
    {
        optionsKey: 'reportTypeOptions',
        rawKey: 'reportTypeOptionsRaw',
        setKey: 'setReportTypeOptions',
        refreshKey: 'refreshReportTypeOptions',
    }
);

// Project（attr_id=2）: API（/get_all_attributes）から取得
export const projectOptionsState = atom<OptionsBase[]>([]);
export const useProjectOptions = createNamedAttrHook(
    ATTRIBUTE_ID_PROJECT,
    projectOptionsState,
    'Projectの取得に失敗しました',
    {
        optionsKey: 'projectOptions',
        rawKey: 'projectOptionsRaw',
        setKey: 'setProjectOptions',
        refreshKey: 'refreshProjectOptions',
    }
);

// パワートレイン（attr_id=3）
export const powerTrainOptionsState = atom<OptionsBase[]>([]);
export const usePowerTrainOptions = createNamedAttrHook(
    ATTRIBUTE_ID_POWERTRAIN,
    powerTrainOptionsState,
    'パワートレインの取得に失敗しました',
    {
        optionsKey: 'powerTrainOptions',
        rawKey: 'powerTrainOptionsRaw',
        setKey: 'setPowerTrainOptions',
        refreshKey: 'refreshPowerTrainOptions',
    }
);

// グレード（attr_id=4）
export const gradeOptionsState = atom<OptionsBase[]>([]);
export const useGradeOptions = createNamedAttrHook(
    ATTRIBUTE_ID_GRADE,
    gradeOptionsState,
    'グレードの取得に失敗しました',
    {
        optionsKey: 'gradeOptions',
        rawKey: 'gradeOptionsRaw',
        setKey: 'setGradeOptions',
        refreshKey: 'refreshGradeOptions',
    }
);

// 仕向け（attr_id=5）
export const shimukeOptionsState = atom<OptionsBase[]>([]);
export const useShimukeOptions = createNamedAttrHook(
    ATTRIBUTE_ID_SHIMUKE,
    shimukeOptionsState,
    '仕向けの取得に失敗しました',
    {
        optionsKey: 'shimukeOptions',
        rawKey: 'shimukeOptionsRaw',
        setKey: 'setShimukeOptions',
        refreshKey: 'refreshShimukeOptions',
    }
);

// ロット（attr_id=6）
export const lotOptionsState = atom<OptionsBase[]>([]);
export const useLotOptions = createNamedAttrHook(
    ATTRIBUTE_ID_LOT,
    lotOptionsState,
    'ロットの取得に失敗しました',
    {
        optionsKey: 'lotOptions',
        rawKey: 'lotOptionsRaw',
        setKey: 'setLotOptions',
        refreshKey: 'refreshLotOptions',
    }
);

// --- フリー項目 ---
// フリー項目は API（/get_all_attributes）から取得し、
// 固定属性(attr_id:1..6)を除外したうえで、表示時に org で正規化する
export const freeOptionsState = atom<OptionsBase[]>([]);

export const useFreeOptions = (org?: { department_id?: number; team_id?: number }) => {
    const [raw, setRaw] = useAtom(freeOptionsState);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue, setRefreshValue] = useAtom(refreshMasterOptionsState);

    const refresh = () => setRefreshValue(Date.now());

    const list: any[] = Array.isArray(raw) ? (raw as any[]) : [];
    const depId = toOrgId(org?.department_id ?? 0);
    const teamId = toOrgId(org?.team_id ?? 0);
    const hasOrg = depId > 0;

    const normalizedRaw = hasOrg
        ? normalizeAttributesForSelection(list as OptionsBase[], depId, teamId, { includeAttrTypeInKey: true })
        : list;
    const options = normalizedRaw
        .filter((item: any) => isItemEnabled((item as any)?.status))
        .map(sanitizeItem);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            // 組織指定がある場合は組織単位の取得済み判定、無い場合は従来通り保持データで判定
            if (hasOrg) {
                if (hasFetchedAttributesForOrg(depId, teamId) && !refreshValue) return;
            } else if ((list?.length ?? 0) > 0 && !refreshValue) {
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const attrs = hasOrg
                    ? await fetchAttributeOptionsForOrg(depId, teamId, Boolean(refreshValue))
                    : await fetchAttributeOptions(Boolean(refreshValue));
                if (cancelled) return;

                const fixedNameSet = new Set<string>(
                    (attrs ?? [])
                        .filter((a: any) => FIXED_ATTR_ID_SET.has(Number(a?.attr_id)))
                        .map((a: any) => getAttrNameKey(a))
                        .filter(Boolean)
                );

                const free = (attrs ?? [])
                    // 固定属性(attr_id:1..6)除外
                    .filter((a: any) => !FIXED_ATTR_ID_SET.has(Number(a?.attr_id)))
                    // 固定項目の枝番(attr_id!=1..6)も除外
                    .filter((a: any) => !fixedNameSet.has(getAttrNameKey(a)))
                    // 保存後の再取得ではチーム単位の取得結果のみがキャッシュに入り、固定属性(attr_id:1..6)の行が
                    // 存在しないため名前での除外が効かない。APIが返す logical_attr_id(1..6)で枝番を除外する。
                    .filter((a: any) => !FIXED_ATTR_ID_SET.has(Number(a?.logical_attr_id)));

                setRaw(free);
            } catch (err) {
                setError('フリー項目の取得に失敗しました');
                console.error('Failed to load free options:', err);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [refreshValue, setRaw, hasOrg, depId, teamId]);

    return {
        freeOptions: options,
        freeOptionsRaw: normalizedRaw,
        setFreeOptions: setRaw,
        isLoading,
        error,
        refreshFreeOptions: refresh,
    };
};

const SELECT_ATTR_TYPES = new Set([11, 12, 13]);

export const useSortableAttributes = (): OptionsBase[] => {
    const [raw] = useAtom(freeOptionsState);
    return useMemo(() => {
        if (!Array.isArray(raw)) return [];
        return raw.filter((a: any) =>
            Boolean((a as any).use_for_sort) &&
            SELECT_ATTR_TYPES.has(Number((a as any).attr_type))
        );
    }, [raw]);
};

export const initialFreeOptionState = atom<OptionsBase>(
    {
        attr_id: -1,
        label: '',
        department_id: 0,
        type: '',
        required: false,
        multiple_select: false,
        status: true,
        options: []
    }
);

// 承認者-3と承認者AMのリスト
export interface ApproveMember {
    department_id: number;
    team_id: number;
    name: string;
    approve_3: number;
    approve_am: number;
}

/* =========================
   承認者リスト: キャッシュ対応
 * ========================= */
let approveMemberListCache: ApproveMember[] | null = null;
let approveMemberListCachePromise: Promise<ApproveMember[]> | null = null;
let approveMemberListCacheToken: number | null = null;
let approveMemberListReqSeq = 0;
let approveMemberListLatestReq = 0;
export const invalidateApproveMemberListCache = () => {
    // 進行中の取得はここでは破棄しない（重複リクエスト防止）
    approveMemberListCache = null;
};

// APIから承認者リストを取得（エンドポイント: /get_all_manage_members）（キャッシュ対応）
const fetchApproveMemberList = async (force = false): Promise<ApproveMember[]> => {
    const fallbackData: ApproveMember[] = [];

    if (!force && approveMemberListCache) return approveMemberListCache;
    if (force && approveMemberListCache && approveMemberListCacheToken === masterRefreshToken) return approveMemberListCache;
    if (approveMemberListCachePromise) return approveMemberListCachePromise;

    const reqId = ++approveMemberListReqSeq;
    approveMemberListLatestReq = reqId;
    approveMemberListCachePromise = fetchOptionsFromAPI(API_ENDPOINTS.GET_ALL_MANAGE_MEMBERS, fallbackData)
        .then((data: ApproveMember[]) => {
            if (reqId === approveMemberListLatestReq) {
                approveMemberListCache = data;
                approveMemberListCacheToken = masterRefreshToken;
            }
            approveMemberListCachePromise = null;
            return data;
        })
        .catch((err) => {
            approveMemberListCachePromise = null;
            throw err;
        });

    return approveMemberListCachePromise;
};

// グローバル状態
export const approveMemberListState = atom<ApproveMember[]>([]);

// 取得用フック
export const useApproveMemberList = () => {
    const [approveMemberList, setApproveMemberList] = useAtom(approveMemberListState);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue, setRefreshValue] = useAtom(refreshMasterOptionsState);

    const refreshApproveMemberList = () => setRefreshValue(Date.now());

    useEffect(() => {
        const load = async () => {
            // 既に保持済みかつ明示リフレッシュでない場合はスキップ
            if ((approveMemberList?.length ?? 0) > 0 && !refreshValue) return;

            setIsLoading(true);
            setError(null);
            try {
                const list = await fetchApproveMemberList(Boolean(refreshValue));
                setApproveMemberList(list);
            } catch (err) {
                setError('承認者リストの取得に失敗しました');
                console.error('Failed to load approve member list:', err);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [refreshValue, setApproveMemberList]);

    return { approveMemberList, setApproveMemberList, isLoading, error, refreshApproveMemberList };
};
 
/* =========================
   Docset ID List: キャッシュ対応
 * ========================= */
export interface DocsetIdItem {
    enlight_team: string;
    enlight_subteam: string;
    docset_id: number;
    description: string;
    department_id: number;
    team_id: number;
}

let docsetIdListCache: DocsetIdItem[] | null = null;
let docsetIdListCachePromise: Promise<DocsetIdItem[]> | null = null;
let docsetIdListCacheToken: number | null = null;
let docsetIdListReqSeq = 0;
let docsetIdListLatestReq = 0;
export const invalidateDocsetIdListCache = () => {
    // 進行中の取得はここでは破棄しない（重複リクエスト防止）
    docsetIdListCache = null;
};

// APIからDocset ID Listを取得（エンドポイント: /get_docset_id_list）（キャッシュ対応）
const fetchDocsetIdList = async (force = false): Promise<DocsetIdItem[]> => {
    const fallbackData: DocsetIdItem[] = [];

    if (!force && docsetIdListCache) return docsetIdListCache;
    if (force && docsetIdListCache && docsetIdListCacheToken === masterRefreshToken) return docsetIdListCache;
    if (docsetIdListCachePromise) return docsetIdListCachePromise;

    const endpoint = '/get_docset_id_list';
    const reqId = ++docsetIdListReqSeq;
    docsetIdListLatestReq = reqId;
    docsetIdListCachePromise = fetchOptionsFromAPI(endpoint, fallbackData)
        .then((data: DocsetIdItem[]) => {
            if (reqId === docsetIdListLatestReq) {
                docsetIdListCache = data;
                docsetIdListCacheToken = masterRefreshToken;
            }
            docsetIdListCachePromise = null;
            return data;
        })
        .catch((err) => {
            docsetIdListCachePromise = null;
            throw err;
        });
    
    return docsetIdListCachePromise;
};

// グローバル状態
export const docsetIdListState = atom<DocsetIdItem[]>([]);

// 取得用フック
export const useDocsetIdList = () => {
    const [docsetIdList, setDocsetIdList] = useAtom(docsetIdListState);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue, setRefreshValue] = useAtom(refreshMasterOptionsState);

    const refreshDocsetIdList = () => setRefreshValue(Date.now());

    useEffect(() => {
        const load = async () => {
            // 既に保持済みかつ明示リフレッシュでない場合はスキップ
            if ((docsetIdList?.length ?? 0) > 0 && !refreshValue) return;

            setIsLoading(true);
            setError(null);
            try {
                const list = await fetchDocsetIdList(Boolean(refreshValue));
                setDocsetIdList(list);
            } catch (err) {
                setError('Docset ID リストの取得に失敗しました');
                console.error('Failed to load docset id list:', err);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [refreshValue, setDocsetIdList]);

    return { docsetIdList, setDocsetIdList, isLoading, error, refreshDocsetIdList };
};

/* =========================
   チーム分類（Team Classifications）: キャッシュ対応
 * ========================= */
let teamClassificationsCache: any[] | null = null;
let teamClassificationsCachePromise: Promise<any[]> | null = null;
let teamClassificationsCacheToken: number | null = null;
let teamClassificationsReqSeq = 0;
let teamClassificationsLatestReq = 0;
export const invalidateTeamClassificationsCache = () => {
    // 進行中の取得はここでは破棄しない（重複リクエスト防止）
    teamClassificationsCache = null;
};

// APIからチーム分類一覧を取得（エンドポイント: /get_all_team_classfications）（キャッシュ対応）
const fetchTeamClassifications = async (force = false): Promise<any[]> => {
    const fallbackData: any[] = [];

    if (!force && teamClassificationsCache) return teamClassificationsCache;
    if (force && teamClassificationsCache && teamClassificationsCacheToken === masterRefreshToken) return teamClassificationsCache;
    if (teamClassificationsCachePromise) return teamClassificationsCachePromise;

    const reqId = ++teamClassificationsReqSeq;
    teamClassificationsLatestReq = reqId;
    teamClassificationsCachePromise = fetchOptionsFromAPI('/get_all_team_classfications', fallbackData)
        .then((data: any) => {
            const list = Array.isArray(data)
                ? data
                : Array.isArray((data as any)?.team_classfications)
                    ? (data as any).team_classfications
                    : Array.isArray((data as any)?.team_classifications)
                        ? (data as any).team_classifications
                        : Array.isArray((data as any)?.list)
                            ? (data as any).list
                            : Array.isArray((data as any)?.data)
                                ? (data as any).data
                                : [];
            if (reqId === teamClassificationsLatestReq) {
                teamClassificationsCache = list;
                teamClassificationsCacheToken = masterRefreshToken;
            }
            teamClassificationsCachePromise = null;
            return list;
        })
        .catch((err) => {
            teamClassificationsCachePromise = null;
            throw err;
        });

    return teamClassificationsCachePromise;
};

// グローバル状態
const teamClassificationsState = atom<any[]>([]);

// 取得用フック
export const useTeamClassifications = () => {
    const [teamClassifications, setTeamClassifications] = useAtom(teamClassificationsState);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshValue] = useAtom(refreshMasterOptionsState);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            // 既に保持済みかつ明示リフレッシュでない場合はスキップ
            if ((teamClassifications?.length ?? 0) > 0 && !refreshValue) return;

            setIsLoading(true);
            setError(null);
            try {
                const list = await fetchTeamClassifications(Boolean(refreshValue));
                if (!cancelled) setTeamClassifications(list);
            } catch (err) {
                if (!cancelled) {
                    setError('チーム分類の取得に失敗しました');
                    console.error('Failed to load team classifications:', err);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshValue, setTeamClassifications]);

    return { teamClassifications, isLoading, error };
};


/* =========================
   まとめ操作（全キャッシュ無効化／事前ロード／一括リフレッシュ）
 * ========================= */
export const invalidateAllMasterOptionsCache = () => {
    invalidateDivisionOptionsCache();
    invalidateTeamOptionsCache();
    invalidateUserOptionsCache();
    invalidateAttributeOptionsCache();
    invalidateCommonOptionsCache();
    invalidateApproveMemberListCache();
    invalidateDocsetIdListCache();
    invalidateTeamClassificationsCache();
    // リフレッシュトークンを進め、同一サイクル中の force 再取得を1回に集約
    bumpMasterRefreshToken();
};

/**
 * 初回表示や再レンダリング時にまとめて API を1回ずつだけ叩くための支援フック
 * - preloadAllMasterOptions: キャッシュを温める（再描画トリガーは行わない）
 * - refreshAllMasterOptions: 全キャッシュを無効化→最新をまとめて取得→リフレッシュatomで購読側を更新
 */
export const useMasterOptionsRefresher = () => {
    const [, setRefreshValue] = useAtom(refreshMasterOptionsState);

    const preloadAllMasterOptions = useCallback(async () => {
        // 初期表示時のキャッシュ温めは行う（並列取得）
        // 属性(attributes)は課・チームごとのスコープ付き取得に切り替えたため、
        // ここでの全件バルク先読みは行わない（各利用側フックが org 指定で個別に取得する）
        await Promise.all([
            fetchDivisionOptions(false),
            fetchTeamOptions(false),
            fetchUserOptions(false),
            fetchCommonOptios(false),
            fetchApproveMemberList(false),
            fetchDocsetIdList(false),
            fetchTeamClassifications(false),
        ]);
    }, []);

    const refreshAllMasterOptions = useCallback(() => {
        // StrictModeや複数コンポーネントから同時に呼ばれても、更新サイクル全体を1本に集約する。
        return shareInFlightRequest('master-options:refresh-all', async () => {
            // 先に最新データの取得を完了し、その後で購読側へ一度だけ通知する。
            // 画面遷移中でも、購読側が古い atom を再描画する競合を発生させない。
            invalidateAllMasterOptionsCache();
            // 属性(attributes)は課・チームごとのスコープ付き取得に切り替えたため、
            // ここでの全件バルク再取得は行わない（各利用側フックが refreshValue の更新を検知して
            // 表示中の org 分だけを再取得する）
            await Promise.all([
                fetchDivisionOptions(true),
                fetchTeamOptions(true),
                fetchCommonOptios(true),
                fetchApproveMemberList(true),
                fetchDocsetIdList(true),
                fetchTeamClassifications(true),
            ]);
            setRefreshValue((value) => value + 1);
        });
    }, [setRefreshValue]);

    return { preloadAllMasterOptions, refreshAllMasterOptions, invalidateAllMasterOptionsCache };
};

/* =========================
   部門/チームのみのリフレッシュ（他項目は触らない）
 * ========================= */
export const useOrgMastersRefresher = () => {
    const [, setRefreshValue] = useAtom(refreshMasterOptionsState);

    const refreshOrgMastersOnly = async () => {
        // 部門/チームのみキャッシュ無効化＋リフレッシュトークン更新
        invalidateDivisionOptionsCache();
        invalidateTeamOptionsCache();
        bumpMasterRefreshToken();

        // 購読側へリフレッシュ通知
        setRefreshValue(Date.now());

        // 取得は部門/チームのみ（他の heavy な属性群は触らない）
        await Promise.all([
            fetchDivisionOptions(true),
            fetchTeamOptions(true),
        ]);
    };

    return { refreshOrgMastersOnly };
};
// Enlightチームカラム設定・強制紐付けリスト
export const ENLIGHT_TEAM_COLUMN_ABSOLUTE_LIST = [
    {'TITLE': 'タイトル'},
    {'BODY': '本文'},
    {'F/B': 'フィードバック'},
    {'APPROVER': '職制'},
    {'RELATEDMEMBERS': '連名者'},
    {'DAY&TIME_START': '開始日付'},
    {'DAY&TIME_END': '終了日付'},
    {'CREATE_BY': '記入者'},
];
